/**
 * API Client Utility
 * Centralized HTTP client for making API requests with authentication and error handling
 */

import { API_CONFIG, API_ENDPOINTS } from '../config/api.config';
import authService from '../auth/authService';

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: unknown;
  requireAuth?: boolean;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  statusText: string;
}

export class ApiError extends Error {
  statusCode: number;
  response?: unknown;

  constructor(message: string, statusCode: number, response?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.response = response;
  }
}

/** Extract a user-facing message from API error response bodies (PACS, validation, etc.) */
export function extractApiErrorMessage(errorData: unknown, fallback = 'Request failed'): string {
  if (!errorData || typeof errorData !== 'object') {
    return fallback;
  }

  const errorObj = errorData as {
    message?: string;
    error?: string | { errorMessage?: string; message?: string };
    errors?: Array<{ msg?: string; message?: string; path?: string }>;
  };

  if (errorObj.error && typeof errorObj.error === 'object' && errorObj.error !== null) {
    const nestedMessage = errorObj.error.errorMessage?.trim() || errorObj.error.message?.trim();
    if (nestedMessage) {
      return nestedMessage;
    }
  }

  if (typeof errorObj.error === 'string' && errorObj.error.trim()) {
    return errorObj.error.trim();
  }

  if (errorObj.errors && Array.isArray(errorObj.errors) && errorObj.errors.length > 0) {
    return errorObj.errors
      .map(err => err.msg || err.message || 'Validation error')
      .filter(Boolean)
      .join('; ');
  }

  if (errorObj.message?.trim()) {
    return errorObj.message.trim();
  }

  return fallback;
}

/**
 * Make an API request with automatic authentication and error handling
 */
async function parseErrorResponse(
  response: Response,
  fallbackMessage: string
): Promise<{ errorMessage: string; errorData: unknown }> {
  let errorMessage = fallbackMessage;
  let errorData: unknown;

  try {
    errorData = await response.json();
    if (typeof errorData === 'object' && errorData !== null) {
      errorMessage = extractApiErrorMessage(errorData, errorMessage);
    }
  } catch {
    // If response is not JSON, try to get text
    try {
      const text = await response.text();
      errorMessage = text || errorMessage;
    } catch {
      // Ignore parsing errors
    }
  }

  return { errorMessage, errorData };
}

function throwIfBusinessFailure(data: unknown, status: number): void {
  if (!data || typeof data !== 'object') {
    return;
  }
  const responseData = data as { success?: boolean; errors?: unknown[] };
  if (responseData.success === false) {
    throw new ApiError(extractApiErrorMessage(data, 'Request failed'), status, data);
  }
}

async function parseSuccessBody<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type');
  const text = await response.text();

  if (response.status === 304 || !text.trim()) {
    throw new ApiError(
      'Empty or cached response from server. Please refresh and try again.',
      response.status
    );
  }

  if (contentType && contentType.includes('application/json')) {
    let data: T;
    try {
      data = JSON.parse(text) as T;
    } catch {
      throw new ApiError('Invalid JSON response from server', response.status);
    }
    throwIfBusinessFailure(data, response.status);
    return data;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError(
      text.startsWith('<')
        ? 'Unexpected HTML response from server'
        : 'Invalid response from server',
      response.status
    );
  }
}

function buildRequestHeaders(
  headers: Record<string, string>,
  isFormData: boolean,
  requireAuth: boolean
): Record<string, string> {
  const requestHeaders: Record<string, string> = {
    accept: 'application/json, text/plain, */*',
    'tenant-id': API_CONFIG.TENANT_ID,
    ...headers,
  };

  if (!isFormData) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  if (requireAuth) {
    const authHeader = authService.getAuthHeader();
    if (authHeader.Authorization) {
      requestHeaders.Authorization = authHeader.Authorization;
    }
  }

  return requestHeaders;
}

export async function apiRequest<T>(
  url: string,
  options: ApiRequestOptions = {}
): Promise<ApiResponse<T>> {
  const { method = 'GET', headers = {}, body, requireAuth = true } = options;
  const isFormData = body instanceof FormData;
  const requestHeaders = buildRequestHeaders(headers, isFormData, requireAuth);

  const requestConfig: RequestInit = {
    method,
    headers: requestHeaders,
    cache: 'no-store',
    mode: 'cors',
  };

  if (body) {
    requestConfig.body = isFormData ? body : JSON.stringify(body);
  }

  try {
    const response = await fetch(url, requestConfig);

    if (!response.ok) {
      const { errorMessage, errorData } = await parseErrorResponse(
        response,
        `Request failed with status ${response.status}`
      );
      throw new ApiError(errorMessage, response.status, errorData);
    }

    const data = await parseSuccessBody<T>(response);

    return {
      data,
      status: response.status,
      statusText: response.statusText,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    throw new ApiError(error instanceof Error ? error.message : 'Network request failed', 0, error);
  }
}

/**
 * GET request helper
 */
export async function apiGet<T>(
  url: string,
  options?: Omit<ApiRequestOptions, 'method' | 'body'>
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, { ...options, method: 'GET' });
}

/**
 * POST request helper
 */
export async function apiPost<T>(
  url: string,
  body: unknown,
  options?: Omit<ApiRequestOptions, 'method' | 'body'>
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, { ...options, method: 'POST', body });
}

/**
 * PUT request helper
 */
export async function apiPut<T>(
  url: string,
  body: unknown,
  options?: Omit<ApiRequestOptions, 'method' | 'body'>
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, { ...options, method: 'PUT', body });
}

/**
 * DELETE request helper
 */
export async function apiDelete<T>(
  url: string,
  options?: Omit<ApiRequestOptions, 'method' | 'body'>
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, { ...options, method: 'DELETE' });
}

/**
 * Fetch current authenticated user information
 * Calls the /ris/api/auth/me endpoint
 */
export async function fetchAuthenticatedUser(): Promise<ApiResponse<any>> {
  try {
    const response = await apiGet(API_ENDPOINTS.AUTH.GET_CURRENT_USER, {
      requireAuth: true,
    });

    console.log('User authentication response:', response);
    return response;
  } catch (error) {
    console.error('Failed to fetch authenticated user:', error);
    throw error;
  }
}
